// Design lock listing, summary and mutations.
import {
  CreateDesignLockRequest,
  DesignLockDetailResponse,
  DesignLockListEntry,
  DesignLockListResponse,
  DesignLockMutationResponse,
  DesignLockStatus,
  DesignLockSummaryResponse,
  FetchDesignLocksRequest,
} from '../../types/designLocks';
import { forgeRequest, forgeVoidRequest, invalidateForgeRequests } from '../forgeRequest';
import { FORGE_DESIGN_LOCKS_API_URL } from './endpoints';

// api-dl HttpModel responses serialize with camelCase aliases (e.g. `pageSize`, `designId`),
// so the wire shapes below are camelCase even though request bodies/query params stay snake_case.
type ApiDesignLockStatusInfo = {
  designId: string;
  status: DesignLockStatus;
  cropsChanged: boolean;
  metadataChanged: boolean;
  groupsChanged: boolean;
  lockedActiveCropCount: number;
  liveActiveCropCount: number;
  lockedBy: string;
  lockedAt: string;
  updatedBy?: string | null;
  updatedAt?: string | null;
  contentHashVersion: number;
};

type ApiDesignLockListEntry = ApiDesignLockStatusInfo & {
  normalizedBarcode?: string | null;
  representativeCropId?: string | null;
  representativeVizUrl?: string | null;
  cropCount: number;
};

type ApiDesignLockListResponse = {
  locks: ApiDesignLockListEntry[];
  total: number;
  page: number;
  pageSize: number;
};

function mapDesignLockStatusInfo(lock: ApiDesignLockStatusInfo): DesignLockMutationResponse {
  return {
    designId: lock.designId,
    status: lock.status,
    cropsChanged: lock.cropsChanged,
    metadataChanged: lock.metadataChanged,
    groupsChanged: lock.groupsChanged,
    lockedActiveCropCount: lock.lockedActiveCropCount,
    liveActiveCropCount: lock.liveActiveCropCount,
    lockedBy: lock.lockedBy,
    lockedAt: lock.lockedAt,
    updatedBy: lock.updatedBy,
    updatedAt: lock.updatedAt,
    contentHashVersion: lock.contentHashVersion,
  };
}

function mapDesignLockListEntry(lock: ApiDesignLockListEntry): DesignLockListEntry {
  return {
    ...mapDesignLockStatusInfo(lock),
    normalizedBarcode: lock.normalizedBarcode ?? '',
    representativeCropId: lock.representativeCropId ?? '',
    representativeVizUrl: lock.representativeVizUrl,
    cropCount: lock.cropCount,
  };
}

export async function fetchDesignLocks(
  token: string,
  filters: FetchDesignLocksRequest = {}
): Promise<DesignLockListResponse> {
  const params = new URLSearchParams();
  if (filters.chainId !== undefined && filters.chainId !== null) {
    params.set('chain_id', String(filters.chainId));
  }
  if (filters.status) params.set('status', filters.status);
  const search = filters.search?.trim();
  if (search) params.set('search', search);
  if (filters.page !== undefined) params.set('page', String(filters.page));
  if (filters.pageSize !== undefined) params.set('page_size', String(filters.pageSize));
  const query = params.toString();
  const payload = await forgeRequest<ApiDesignLockListResponse | ApiDesignLockListEntry[]>(
    token,
    `${FORGE_DESIGN_LOCKS_API_URL}${query ? `?${query}` : ''}`,
    'Error fetching design locks',
    { dedupeKey: ['fetchDesignLocks', `${token}:${query}`] }
  );
  if (Array.isArray(payload)) {
    return {
      locks: payload.map(mapDesignLockListEntry),
      total: payload.length,
      page: filters.page ?? 1,
      pageSize: filters.pageSize ?? payload.length,
    };
  }
  return {
    locks: payload.locks.map(mapDesignLockListEntry),
    total: payload.total,
    page: payload.page,
    pageSize: payload.pageSize,
  };
}

export async function fetchDesignLockSummary(
  token: string,
  options: { includeStatusCounts?: boolean; chainId?: number } = {}
): Promise<DesignLockSummaryResponse> {
  const params = new URLSearchParams();
  if (options.includeStatusCounts !== undefined) {
    params.set('include_status_counts', String(options.includeStatusCounts));
  }
  if (options.chainId !== undefined) params.set('chain_id', String(options.chainId));
  const query = params.toString();
  return forgeRequest<DesignLockSummaryResponse>(
    token,
    `${FORGE_DESIGN_LOCKS_API_URL}/summary${query ? `?${query}` : ''}`,
    'Error fetching design lock summary',
    { dedupeKey: ['fetchDesignLockSummary', `${token}:${query}`] }
  );
}

function invalidateDesignLockRequests(): void {
  invalidateForgeRequests('fetchDesignLocks');
  invalidateForgeRequests('fetchDesignLockSummary');
}

export async function createDesignLock(
  token: string,
  request: CreateDesignLockRequest
): Promise<DesignLockMutationResponse> {
  const response = await forgeRequest<ApiDesignLockStatusInfo>(
    token,
    FORGE_DESIGN_LOCKS_API_URL,
    'Error creating design lock',
    { method: 'POST', body: request }
  );
  invalidateDesignLockRequests();
  return mapDesignLockStatusInfo(response);
}

export async function updateDesignLock(
  token: string,
  designId: string,
  chainId: number,
  taskId?: string
): Promise<DesignLockMutationResponse> {
  const params = new URLSearchParams({ chain_id: String(chainId) });
  if (taskId) params.set('task_id', taskId);
  const response = await forgeRequest<ApiDesignLockStatusInfo>(
    token,
    `${FORGE_DESIGN_LOCKS_API_URL}/${encodeURIComponent(designId)}?${params}`,
    'Error updating design lock',
    { method: 'PUT' }
  );
  invalidateDesignLockRequests();
  return mapDesignLockStatusInfo(response);
}

export async function deleteDesignLock(
  token: string,
  designId: string,
  chainId: number,
  taskId?: string
): Promise<void> {
  const params = new URLSearchParams({ chain_id: String(chainId) });
  if (taskId) params.set('task_id', taskId);
  await forgeVoidRequest(
    token,
    `${FORGE_DESIGN_LOCKS_API_URL}/${encodeURIComponent(designId)}?${params}`,
    'Error deleting design lock',
    { method: 'DELETE' }
  );
  invalidateDesignLockRequests();
}

export async function fetchDesignLock(
  token: string,
  designId: string,
  filters: { chainId: number; includeCrops?: boolean }
): Promise<DesignLockDetailResponse> {
  const params = new URLSearchParams({
    chain_id: String(filters.chainId),
    include_crops: String(filters.includeCrops ?? false),
  });
  return mapDesignLockListEntry(
    await forgeRequest<ApiDesignLockListEntry>(
      token,
      `${FORGE_DESIGN_LOCKS_API_URL}/${encodeURIComponent(designId)}?${params}`,
      'Error fetching design lock'
    )
  );
}
